import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, List, ListItem, ListItemButton, ListItemText, Paper, Skeleton, Stack, Typography } from '@mui/material'
import { format, formatDistanceToNowStrict } from 'date-fns'
import { nb } from 'date-fns/locale'
import { EventSnippet } from '../../../interfaces/IEvent'
import { usePolling } from '../../../hooks/usePolling'
import { bucketColor } from './BucketColors'
import InfoTooltip from './InfoTooltip'

interface ActiveIncidentsCardProps {
  variant?: 'compact' | 'wall'
  info?: string
}

// Speiler StatsHandler.IsCustomerImpactResolved og CustomerEventDto.MapStatus —
// disse statusene presenteres som "Closed" til bruker selv om eventet er teknisk åpent.
const RESOLVED_STATUSES = new Set<string>(['Provisorisk løst', 'Evaluering', 'Oppfølging', 'Lukket'])

function ActiveIncidentsCard({ variant = 'compact', info }: ActiveIncidentsCardProps) {
  const navigate = useNavigate()
  const isWall = variant === 'wall'
  const { data } = usePolling<EventSnippet[]>('/api/events/incident', 60_000)

  const active = useMemo(() => {
    if (!data) return []
    return data
      .filter((e) => !RESOLVED_STATUSES.has(e.status as unknown as string))
      .sort((a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime())
  }, [data])

  const totalActive = useMemo(() => {
    if (!data) return 0
    return data.filter((e) => !RESOLVED_STATUSES.has(e.status as unknown as string)).length
  }, [data])

  let body
  if (!data) {
    body = (
      <List dense disablePadding>
        {Array.from({ length: 3 }).map((_, i) => (
          <ListItem key={i} sx={{ borderLeft: '4px solid', borderColor: 'divider', mb: 0, py: 0.5 }}>
            <ListItemText
              primary={<Skeleton variant="text" width="60%" />}
              secondary={<Skeleton variant="text" width="40%" />}
            />
          </ListItem>
        ))}
      </List>
    )
  } else if (active.length === 0) {
    body = <Typography variant="body2" color="text.secondary">Ingen pågående fellesfeil</Typography>
  } else {
    body = (
      <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
        <List dense disablePadding>
          {active.map((e) => {
            const border = bucketColor(e.criticality as unknown as string)
            const age = formatDistanceToNowStrict(new Date(e.createdOn), { locale: nb })
            return (
              <ListItem key={e.id} disablePadding sx={{ borderLeft: `4px solid ${border}`, mb: 0 }}>
                <ListItemButton onClick={() => navigate(`/events/${e.id}`)} sx={{ py: 0.25 }}>
                  <ListItemText
                    sx={{ my: 0 }}
                    slotProps={{ secondary: { variant: 'caption' } }}
                    primary={
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
                        <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                          {e.id}
                        </Typography>
                        <Typography variant="body2" component="span" noWrap>
                          {e.title}
                        </Typography>
                      </Stack>
                    }
                    secondary={`${e.criticality} · ${e.status} · ${age} (${format(new Date(e.createdOn), 'dd.MM HH:mm', { locale: nb })})`}
                  />
                </ListItemButton>
              </ListItem>
            )
          })}
        </List>
      </Box>
    )
  }

  return (
    <Paper
      elevation={1}
      sx={{
        p: isWall ? { xs: 2, md: 3 } : 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: isWall ? '45vh' : 480,
        minHeight: 0,
      }}
    >
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline', mb: 1, flexShrink: 0 }}>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          <Typography
            variant={isWall ? undefined : 'h6'}
            sx={{ fontSize: isWall ? 'clamp(1.25rem, 2vw, 2.25rem)' : undefined, fontWeight: 600 }}
          >
            Pågående fellesfeil
          </Typography>
          {info && !isWall && <InfoTooltip text={info} />}
        </Stack>
        <Typography
          sx={{ fontSize: isWall ? 'clamp(1.5rem, 2.5vw, 2.75rem)' : undefined, fontWeight: 700 }}
          variant={isWall ? undefined : 'h6'}
          color={totalActive > 0 ? undefined : 'text.secondary'}
        >
          {data ? totalActive : '—'}
        </Typography>
      </Stack>
      {body}
    </Paper>
  )
}

export default ActiveIncidentsCard
